import { postgresSearch } from './postgres/search-service';
import logger from '../utils/logger';

/**
 * Search Service
 * 
 * This service provides search functionality for the application using PostgreSQL for full-text search
 * while maintaining Firebase as the main database. It handles searching for users, techniques, trainings, and posts.
 */
class SearchService {
  private static instance: SearchService;
  private isInitialized: boolean = false;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): SearchService {
    if (!this.instance) {
      this.instance = new SearchService();
    }
    return this.instance;
  }

  /**
   * Initialize the search service
   */
  public async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    try {
      // Initialize PostgreSQL search service
      const postgresInitialized = await postgresSearch.initialize();
      
      if (postgresInitialized) {
        logger.info('Search service initialized with PostgreSQL');
        this.isInitialized = true;
        return true;
      } else {
        logger.warn('Failed to initialize PostgreSQL search service, falling back to Firebase');
        return false;
      }
    } catch (error) {
      logger.error('Error initializing search service', error);
      return false;
    }
  }

  /**
   * Check if the search service is healthy and available
   */
  public async isHealthy(): Promise<boolean> {
    try {
      return await postgresSearch.isHealthy();
    } catch (error) {
      logger.error('Search service health check failed', error);
      return false;
    }
  }

  /**
   * Index a user for search
   * @param userData User data from Firebase
   */
  public async indexUser(userData: any): Promise<boolean> {
    try {
      return await postgresSearch.indexUser(userData);
    } catch (error) {
      logger.error('Error indexing user for search', error);
      return false;
    }
  }

  /**
   * Index a technique for search
   * @param techniqueData Technique data from Firebase
   */
  public async indexTechnique(techniqueData: any): Promise<boolean> {
    try {
      return await postgresSearch.indexTechnique(techniqueData);
    } catch (error) {
      logger.error('Error indexing technique for search', error);
      return false;
    }
  }

  /**
   * Index a training session for search
   * @param trainingData Training data from Firebase
   */
  public async indexTraining(trainingData: any): Promise<boolean> {
    try {
      return await postgresSearch.indexTraining(trainingData);
    } catch (error) {
      logger.error('Error indexing training for search', error);
      return false;
    }
  }

  /**
   * Index a post for search
   * @param postData Post data from Firebase
   */
  public async indexPost(postData: any): Promise<boolean> {
    try {
      return await postgresSearch.indexPost(postData);
    } catch (error) {
      logger.error('Error indexing post for search', error);
      return false;
    }
  }

  /**
   * Delete indexed data for an item
   * @param id Item ID
   * @param type Item type (user, technique, training, post)
   */
  public async deleteIndexedItem(id: string, type: 'user' | 'technique' | 'training' | 'post'): Promise<boolean> {
    try {
      return await postgresSearch.deleteIndexedItem(id, type);
    } catch (error) {
      logger.error(`Error deleting indexed ${type}`, error);
      return false;
    }
  }

  /**
   * Search for users
   * @param query Search query
   * @param limit Maximum number of results
   * @param offset Pagination offset
   * @param includePrivate Whether to include private profiles (requires admin)
   */
  public async searchUsers(query: string, limit = 10, offset = 0, includePrivate = false): Promise<any[]> {
    try {
      // Log the search attempt
      logger.info(`Searching for users with query: "${query}", limit: ${limit}, offset: ${offset}`);
      
      // Check PostgreSQL search health
      const postgresHealthy = await this.isHealthy();
      logger.info(`PostgreSQL search service health: ${postgresHealthy}`);
      
      if (!postgresHealthy) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }

      // Execute PostgreSQL search
      const results = await postgresSearch.searchUsers(query, limit, offset);
      logger.info(`PostgreSQL search returned ${results.length} results`);
      
      // Filter out private profiles unless includePrivate is true
      const filtered = this.filterPrivateProfiles(results, includePrivate);
      logger.info(`After filtering: ${filtered.length} results`);
      return filtered;
    } catch (error) {
      logger.error('Error searching users in PostgreSQL:', error);
      return [];
    }
  }
  
  /**
   * Filter out private profiles from search results
   */
  private filterPrivateProfiles(users: any[], includePrivate: boolean): any[] {
    if (includePrivate) {
      return users;
    }
    
    return users.filter(user => {
      // Check if user has privacy settings
      const profileVisibility = user.preferences?.privacySettings?.profileVisibility;
      
      // If no privacy settings, default to public
      if (!profileVisibility) {
        return true;
      }
      
      // Only include public profiles
      return profileVisibility === 'public';
    });
  }

  /**
   * Search for techniques
   * @param query Search query
   * @param limit Maximum number of results
   * @param offset Pagination offset
   */
  public async searchTechniques(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL search health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search
      const results = await postgresSearch.searchTechniques(query, limit, offset);
      logger.info(`PostgreSQL search returned ${results.length} technique results`);
      return results;
    } catch (error) {
      logger.error('Error searching techniques in PostgreSQL:', error);
      return [];
    }
  }

  /**
   * Search for trainings
   * @param query Search query
   * @param limit Maximum number of results
   * @param offset Pagination offset
   */
  public async searchTrainings(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL search health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search
      const results = await postgresSearch.searchTrainings(query, limit, offset);
      logger.info(`PostgreSQL search returned ${results.length} training results`);
      return results;
    } catch (error) {
      logger.error('Error searching trainings in PostgreSQL:', error);
      return [];
    }
  }

  /**
   * Search for posts
   * @param query Search query
   * @param limit Maximum number of results
   * @param offset Pagination offset
   */
  public async searchPosts(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL search health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search
      const results = await postgresSearch.searchPosts(query, limit, offset);
      logger.info(`PostgreSQL search returned ${results.length} post results`);
      return results;
    } catch (error) {
      logger.error('Error searching posts in PostgreSQL:', error);
      return [];
    }
  }

  /**
   * Search for workout routines
   * @param query Search query
   * @param limit Maximum number of results
   * @param offset Pagination offset
   */
  public async searchWorkoutRoutines(query: string, limit = 10, offset = 0): Promise<any[]> {
    try {
      // Check PostgreSQL search health
      if (!await this.isHealthy()) {
        logger.error('PostgreSQL search service is not healthy');
        return [];
      }
      
      // Execute PostgreSQL search
      const results = await postgresSearch.searchWorkoutRoutines(query, limit, offset);
      logger.info(`PostgreSQL search returned ${results.length} workout routine results`);
      return results;
    } catch (error) {
      logger.error('Error searching workout routines in PostgreSQL:', error);
      return [];
    }
  }

  /**
   * Search across all content types
   * @param query Search query
   * @param limit Maximum number of results per type
   * @param offset Pagination offset
   */
  public async searchAll(query: string, limit = 5, offset = 0): Promise<{
    users: any[];
    techniques: any[];
    trainings: any[];
    posts: any[];
    workoutRoutines?: any[];
  }> {
    // Run all searches in parallel
    const [users, techniques, trainings, posts, workoutRoutines] = await Promise.all([
      this.searchUsers(query, limit, offset),
      this.searchTechniques(query, limit, offset),
      this.searchTrainings(query, limit, offset),
      this.searchPosts(query, limit, offset),
      this.searchWorkoutRoutines(query, limit, offset)
    ]);
    
    return {
      users,
      techniques,
      trainings,
      posts,
      workoutRoutines
    };
  }
}

// Export singleton instance
export const searchService = SearchService.getInstance();
export default searchService;